import { AntDesign } from "@expo/vector-icons";
import { router, useFocusEffect } from "expo-router";
import React, { useState } from "react";
import ContentLoader, { Rect } from "react-content-loader/native";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  Pressable,
  TextInput,
  FlatList,
  ScrollView,
  useWindowDimensions,
} from "react-native";
import { useDispatch } from "react-redux";

import Title from "@/components/Title";
import { setForm } from "@/context/actions/actions";
import { sendRequest } from "@/utils/ApiUtil";
import { generateApplicationsXlsx } from "@/utils/management/ExportApplications";
import { getDisplayedPages } from "@/utils/management/Pages";

export default function Applications() {
  const [isLoading, setIsLoading] = useState(true);
  const [applicationsList, setApplicationsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { height, width } = useWindowDimensions();
  const pageLimit = 15;

  const applcationStatus = {
    READY_FOR_SUBMISSION: t(
      "management/applications:status.readyforsubmission"
    ),
    INCOMPLETE: t("management/applications:status.incomplete"),
    CHANGES_REQUESTED: t("management/applications:status.changesrequested"),
    SUBMITTED: t("management/applications:status.submitted"),
    COMPLETED: t("management/applications:status.completed"),
    PROCESSING: t("management/applications:status.processing"),
  };

  useFocusEffect(
    React.useCallback(() => {
      dispatch(
        setForm({ currentMenuItem: t("management/home:menu.applications") })
      );
      getApplication();
      return () => {
        setCurrentPage(1);
        setSearchQuery("");
      };
    }, [])
  );

  async function getApplication() {
    try {
      const request = await sendRequest(
        "get",
        "/admin/applications",
        null,
        true
      );
      setApplicationsList(request);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderPageIndicator = ({ item }: { item: number }) => (
    <Pressable onPress={() => handlePageChange(item)} className="mx-1">
      <View
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          currentPage === item ? "bg-purple1" : "bg-gray"
        }`}
      >
        <Text
          className={`${currentPage === item ? "text-white" : "text-black1"}`}
        >
          {item}
        </Text>
      </View>
    </Pressable>
  );

  const filteredApplications =
    applicationsList.filter(
      (application) =>
        (
          application.customerDetailsIndividual?.firstName ||
          application.customerDetailsCorporation?.registeredName ||
          application.id
        )
          ?.toLowerCase()
          .includes(searchQuery?.toLowerCase())
    ) || [];

  const paginatedApplications = searchQuery
    ? filteredApplications
    : applicationsList.slice(
        (currentPage - 1) * pageLimit,
        currentPage * pageLimit
      );

  const totalPages = Math.ceil(
    (searchQuery ? filteredApplications : applicationsList).length / pageLimit
  );

  return (
    <ScrollView contentContainerClassName="flex-grow px-4 py-2 bg-white1">
      <View className="w-full items-center my-2">
        <Title text={t("management/applications:title")} />
      </View>
      {isLoading ? (
        <View className="w-full items-center">
          <ContentLoader
            viewBox={`0 0 ${width} ${height}`}
            backgroundColor="#F9F9F9"
            foregroundColor="#E4E4E4"
          >
            <Rect x="0" y="0" rx="5" ry="5" width={width} height={height} />
          </ContentLoader>
        </View>
      ) : (
        <View className="w-full">
          <View className="w-full flex-row justify-between my-4">
            <View>
              <View className="absolute left-4 top-2.5">
                <AntDesign name="search1" size={16} color="#BDBDBD" />
              </View>
              <TextInput
                className="border text-sm border-midgray rounded-full pl-10 py-2 text-base"
                placeholder={t("management/applications:search")}
                placeholderTextColor="#BDBDBD"
                value={searchQuery}
                onChangeText={(text) => setSearchQuery(text)}
              />
            </View>
            <Pressable
              className="flex-col lg:flex-row gap-x-2 items-center justify-center"
              onPress={() =>
                generateApplicationsXlsx(applicationsList, "all_izziwallet", t)
              }
            >
              <AntDesign name="download" size={18} color="#BDBDBD" />
              <Text className="text-midgray text-xs lg:text-sm hover:text-darkergray">
                {t("management/applications:download")}
              </Text>
            </Pressable>
          </View>
          <View className="py-2 px-4 w-full flex-row">
            <View className="w-1/3 items-start">
              <Text className="font-thin text-lg text-darkergray">
                {t("management/applications:board.name")}
              </Text>
            </View>
            <View className="w-1/3 items-start">
              <Text className="font-thin text-lg text-darkergray">
                {t("management/applications:board.status")}
              </Text>
            </View>
            <View className="w-1/3 items-start">
              <Text className="font-thin text-lg text-darkergray">
                {t("management/applications:board.type")}
              </Text>
            </View>
          </View>
          <FlatList
            data={paginatedApplications}
            keyExtractor={(item: any, index: number) => index.toString()}
            renderItem={({ item }: any) => (
              <View className="transition-colors delay-0 bg-gray hover:bg-lightgray border border-lightgray flex-row w-full py-2 px-4 rounded-lg my-1.5">
                <View className="w-1/3 items-start">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="font-thin text-sm text-black"
                  >
                    {(item.customerDetailsIndividual?.firstName ||
                      item.customerDetailsCorporation?.registeredName) ??
                      "-"}
                  </Text>
                </View>
                <View className="w-1/3 items-start">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="font-thin text-sm text-black"
                  >
                    {applcationStatus[item.status] ?? "-"}
                  </Text>
                </View>
                <View className="w-1/3 items-start">
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    className="font-thin text-sm text-black"
                  >
                    {item.applicationType ?? "-"}
                  </Text>
                </View>
              </View>
            )}
          />
          <FlatList
            horizontal
            data={getDisplayedPages(totalPages, currentPage)}
            renderItem={renderPageIndicator}
            keyExtractor={(item, index) => index.toString()}
            contentContainerClassName="mt-4 w-full items-center justify-center"
          />
        </View>
      )}
    </ScrollView>
  );
}
